import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  resource,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Browser } from '@core/services';
import { firstValueFrom } from 'rxjs';

/** Departamento seleccionable en el mapa. */
export interface IDepartmentSelection {
  /** Identificador en formato slug; coincide con el `id` del path en el SVG. */
  readonly slug: string;
  /** Nombre para mostrar. */
  readonly name: string;
}

/** Ruta del SVG con los contornos departamentales, servido desde `public/`. */
const MAP_SOURCE = 'img/home/colombia-departments.svg';

/**
 * Recorte del SVG original que encuadra el territorio continental.
 * El archivo viene con `viewBox 0 0 800 533` y bastante margen vacío.
 */
const MAP_VIEW_BOX = '165 8 450 515';

/** Departamentos de Colombia, en el orden en que se listan bajo el mapa. */
export const DEPARTMENTS: readonly IDepartmentSelection[] = [
  { slug: 'amazonas', name: 'Amazonas' },
  { slug: 'antioquia', name: 'Antioquia' },
  { slug: 'arauca', name: 'Arauca' },
  { slug: 'atlantico', name: 'Atlántico' },
  { slug: 'bogota-dc', name: 'Bogotá D.C.' },
  { slug: 'bolivar', name: 'Bolívar' },
  { slug: 'boyaca', name: 'Boyacá' },
  { slug: 'caldas', name: 'Caldas' },
  { slug: 'caqueta', name: 'Caquetá' },
  { slug: 'casanare', name: 'Casanare' },
  { slug: 'cauca', name: 'Cauca' },
  { slug: 'cesar', name: 'Cesar' },
  { slug: 'choco', name: 'Chocó' },
  { slug: 'cordoba', name: 'Córdoba' },
  { slug: 'cundinamarca', name: 'Cundinamarca' },
  { slug: 'guainia', name: 'Guainía' },
  { slug: 'guaviare', name: 'Guaviare' },
  { slug: 'huila', name: 'Huila' },
  { slug: 'la-guajira', name: 'La Guajira' },
  { slug: 'magdalena', name: 'Magdalena' },
  { slug: 'meta', name: 'Meta' },
  { slug: 'narino', name: 'Nariño' },
  { slug: 'norte-de-santander', name: 'Norte de Santander' },
  { slug: 'putumayo', name: 'Putumayo' },
  { slug: 'quindio', name: 'Quindío' },
  { slug: 'risaralda', name: 'Risaralda' },
  { slug: 'san-andres-y-providencia', name: 'San Andrés y Providencia' },
  { slug: 'santander', name: 'Santander' },
  { slug: 'sucre', name: 'Sucre' },
  { slug: 'tolima', name: 'Tolima' },
  { slug: 'valle-del-cauca', name: 'Valle del Cauca' },
  { slug: 'vaupes', name: 'Vaupés' },
  { slug: 'vichada', name: 'Vichada' },
];

/**
 * ### **DepartmentMap**
 *
 * Mapa interactivo de los departamentos de Colombia. Descarga el SVG de contornos y lo
 * reconstruye desde cero: por cada departamento conocido copia únicamente el atributo `d`
 * a un `<path>` nuevo con sus atributos de accesibilidad. Así se descarta el fondo raster
 * que trae el archivo original y solo entra al DOM geometría, no marcado arbitrario.
 *
 * Bajo el mapa se renderiza una rejilla de botones con los mismos departamentos, que es
 * la vía de acceso cuando el SVG no está disponible (por ejemplo durante el render de
 * servidor, donde no hay `DOMParser`).
 */
@Component({
  selector: 'app-department-map',
  imports: [],
  templateUrl: './department-map.component.html',
  styleUrl: './department-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentMap {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly http = inject(HttpClient);
  private readonly browser = inject(Browser);

  /** Departamento marcado como activo desde el exterior. */
  readonly selectedDepartmentSlug = input<string | null>(null);

  /** Emite el departamento elegido por el usuario. */
  readonly departmentSelected = output<IDepartmentSelection>();

  /** Selección interna, sincronizada con el input pero editable al hacer clic. */
  private readonly selectedSlug = linkedSignal(() => this.selectedDepartmentSlug());

  /**
   * Descarga del SVG. Solo corre en navegador: el redibujado depende de `DOMParser`,
   * que no existe en el servidor, y en SSR se muestra la rejilla de botones.
   */
  protected readonly svgResource = resource({
    loader: async (): Promise<string> => {
      if (!this.browser.isBrowser) return '';
      return firstValueFrom(this.http.get(MAP_SOURCE, { responseType: 'text' }));
    },
  });

  /** Marcado del mapa listo para inyectar. */
  protected readonly mapMarkup = computed<SafeHtml | null>(() => {
    const source = this.svgResource.value();
    if (!source) return null;
    return this.sanitizer.bypassSecurityTrustHtml(this.createOutlineMap(source));
  });

  /**
   * Departamentos que el SVG realmente contiene. Si el archivo no cargó se devuelve la
   * lista completa, de modo que la rejilla de botones siga sirviendo como alternativa.
   */
  protected readonly availableDepartments = computed<readonly IDepartmentSelection[]>(() => {
    const source = this.svgResource.value();
    if (!source) return DEPARTMENTS;

    const parsed = new DOMParser().parseFromString(source, 'image/svg+xml');
    const found = DEPARTMENTS.filter((department) =>
      parsed.getElementById(department.slug)?.getAttribute('d'),
    );

    return found.length ? found : DEPARTMENTS;
  });

  /** Selecciona el departamento bajo el cursor. */
  protected onMapClick(event: MouseEvent): void {
    this.selectFromTarget(event.target);
  }

  /** Permite activar un departamento con teclado. */
  protected onMapKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (this.selectFromTarget(event.target)) event.preventDefault();
  }

  /** Marca un departamento y lo notifica al padre. */
  protected selectDepartment(department: IDepartmentSelection): void {
    this.selectedSlug.set(department.slug);
    this.departmentSelected.emit(department);
  }

  /** `true` si el departamento es el activo. */
  protected isSelected(department: IDepartmentSelection): boolean {
    return this.selectedSlug() === department.slug;
  }

  private selectFromTarget(target: EventTarget | null): boolean {
    const path =
      target instanceof Element ? target.closest<SVGPathElement>('[data-department]') : null;
    const slug = path?.dataset['department'];
    const department = DEPARTMENTS.find((candidate) => candidate.slug === slug);
    if (!department) return false;

    this.selectDepartment(department);
    return true;
  }

  /**
   * Reconstruye el mapa a partir del SVG de origen copiando solo la geometría.
   */
  private createOutlineMap(source: string): string {
    const parsed = new DOMParser().parseFromString(source, 'image/svg+xml');
    const namespace = 'http://www.w3.org/2000/svg';

    const map = parsed.createElementNS(namespace, 'svg');
    map.setAttribute('viewBox', MAP_VIEW_BOX);
    map.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    map.setAttribute('role', 'group');
    map.setAttribute('aria-label', 'Departamentos de Colombia');
    map.setAttribute('class', 'colombia-department-map');

    const selectedSlug = this.selectedSlug();

    for (const department of DEPARTMENTS) {
      const pathData = parsed.getElementById(department.slug)?.getAttribute('d');
      if (!pathData) continue;

      const isSelected = department.slug === selectedSlug;
      const path = parsed.createElementNS(namespace, 'path');

      path.setAttribute('id', department.slug);
      path.setAttribute('d', pathData);
      path.setAttribute('data-department', department.slug);
      path.setAttribute('role', 'button');
      path.setAttribute('tabindex', '0');
      path.setAttribute('aria-label', `Explorar ${department.name}`);
      path.setAttribute('aria-pressed', String(isSelected));
      path.setAttribute('fill', isSelected ? 'var(--azulElectrico)' : 'transparent');
      path.setAttribute('stroke', isSelected ? 'var(--azulOscuro)' : 'var(--verdeOscuro)');
      path.setAttribute('stroke-width', '1.25');
      path.setAttribute('vector-effect', 'non-scaling-stroke');

      const title = parsed.createElementNS(namespace, 'title');
      title.textContent = department.name;
      path.append(title);

      map.append(path);
    }

    return map.outerHTML;
  }
}
